// Pure Stage 3 Work Method truth helpers.
//
// Keep programme/readiness decisions here so the top navigation, Stage 3
// cockpit, stage strip and generation boundary cannot each invent a different
// interpretation of the same saved WMS record.

import { deriveSchedules } from './wms-schedules';

type UnknownRecord = Record<string, unknown>;

export type Stage3WorkMethodReason =
    | 'missing-wms'
    | 'fallback-wms'
    | 'fallback-strategy'
    | 'stale-wms'
    | 'missing-activities'
    | 'missing-sequence'
    | 'placeholder-sequence';

export interface Stage3WorkMethodAssessment {
    ready: boolean;
    reasons: Stage3WorkMethodReason[];
    programmeDays: number;
    incompleteActivityIds: string[];
}

function record(value: unknown): UnknownRecord | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

function finiteNonNegative(value: unknown): number | null {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function structuredFrom(value: unknown): UnknownRecord | null {
    const envelope = record(value);
    if (!envelope) return null;
    return record(envelope.structured) || envelope;
}

/**
 * Canonical project programme duration.
 *
 * `duration_working_days` is the document-level elapsed programme and can be
 * shorter than the sum of activity days when work-packs overlap. Older shapes
 * used `total_days` / `days`; summing activities is a legacy-only last resort.
 */
export function getStage3ProgrammeDays(value: unknown): number {
    const structured = structuredFrom(value);
    if (!structured) return 0;
    const programme = record(structured.programme);
    for (const candidate of [
        programme?.duration_working_days,
        programme?.total_days,
        programme?.days,
    ]) {
        const days = finiteNonNegative(candidate);
        if (days !== null) return days;
    }

    const activities = Array.isArray(structured.activities) ? structured.activities : [];
    return activities
        .filter((activity) => record(activity)?.is_recommendation !== true)
        .reduce((sum, activity) => sum + (finiteNonNegative(record(activity)?.programme_days) || 0), 0);
}

/** Resolve the real text carried by a WMS sequence step. */
export function stage3SequenceStepLabel(step: unknown): string {
    if (typeof step === 'string') return step.trim();
    const source = record(step);
    if (!source) return '';
    // `action` is the canonical PROJECT_WMS_BUILDER_PROMPT field. The aliases
    // keep legacy/imported WMS records readable without fabricating content.
    for (const candidate of [
        source.action,
        source.description,
        source.step,
        source.task,
        source.title,
        source.label,
    ]) {
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }
    return '';
}

export function isStage3SequencePlaceholder(step: unknown): boolean {
    const label = stage3SequenceStepLabel(step);
    if (!label) return true;
    return /^(?:sequence\s+)?step\s*\d+[\s.:;-]*$/i.test(label)
        || /^(?:tbc|todo|placeholder)$/i.test(label);
}

/**
 * Canonicalise supported legacy sequence aliases without inventing a label.
 * Missing/placeholder entries remain detectable by the readiness gate.
 */
export function normaliseStage3Sequence(sequence: unknown): UnknownRecord[] {
    if (!Array.isArray(sequence)) return [];
    return sequence.map((step, index) => {
        const source = record(step);
        const action = stage3SequenceStepLabel(step);
        return {
            ...(source || {}),
            order: finiteNonNegative(source?.order) ?? (index + 1),
            action,
        };
    });
}

export function assessStage3StructuredMethod(value: unknown): {
    complete: boolean;
    reasons: Stage3WorkMethodReason[];
    incompleteActivityIds: string[];
} {
    const structured = structuredFrom(value);
    const activities = structured && Array.isArray(structured.activities)
        ? structured.activities.filter((activity) => record(activity)?.is_recommendation !== true)
        : [];
    if (activities.length === 0) {
        return { complete: false, reasons: ['missing-activities'], incompleteActivityIds: [] };
    }

    const reasons = new Set<Stage3WorkMethodReason>();
    const incompleteActivityIds: string[] = [];
    activities.forEach((activity, index) => {
        const source = record(activity) || {};
        const sequence = Array.isArray(source.sequence_of_works) ? source.sequence_of_works : [];
        const missing = sequence.length === 0;
        const placeholder = !missing && sequence.some(isStage3SequencePlaceholder);
        if (!missing && !placeholder) return;
        reasons.add(missing ? 'missing-sequence' : 'placeholder-sequence');
        incompleteActivityIds.push(String(source.id || `activity-${index + 1}`));
    });

    return {
        complete: reasons.size === 0,
        reasons: Array.from(reasons),
        incompleteActivityIds,
    };
}

function djb2Hex(serialised: string): string {
    let hash = 5381;
    for (let index = 0; index < serialised.length; index += 1) {
        hash = (((hash << 5) + hash) + serialised.charCodeAt(index)) | 0;
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Deterministic identity for the accepted Stage 2 repair strategy.
 *
 * The Work Method model is allowed to expand the chosen approach into useful
 * construction language. Its generated `overview.chosen_approach` is therefore
 * prose, not a safe lineage stamp: exact-string comparison made a newly
 * generated method stale whenever the model elaborated the strategy title.
 * Persist this fingerprint on new WMS envelopes and compare fingerprints on
 * reload. Legacy envelopes without the stamp remain scope-checked only rather
 * than guessing from two pieces of prose.
 */
export function computeStrategyHash(strategy: unknown): string {
    const strategyRecord = record(strategy);
    const chosen = record(strategyRecord?.chosen) || strategyRecord;
    if (!chosen) return '';

    const canonical = {
        id: String(chosen.id || strategyRecord?.chosen_id || '').trim(),
        label: typeof chosen.label === 'string' ? chosen.label.trim() : '',
        method_outline: typeof chosen.method_outline === 'string' ? chosen.method_outline.trim() : '',
        narrative: typeof chosen.narrative === 'string' ? chosen.narrative.trim() : '',
    };
    if (!canonical.id && !canonical.label && !canonical.method_outline && !canonical.narrative) return '';
    return djb2Hex(JSON.stringify(canonical));
}

function methodCanon(item: unknown): UnknownRecord {
    const it = record(item) || {};
    const workMethod = record(it.workMethod);
    return {
        id: it.id || '',
        code: it.code || '',
        title: typeof it.title === 'string' ? it.title.trim() : '',
        description: typeof it.description === 'string' ? it.description.trim() : '',
        wm: workMethod ? {
            chosen_approach: workMethod.chosen_approach || '',
            seq: (Array.isArray(workMethod.sequence_of_works) ? workMethod.sequence_of_works : []).map((step) => {
                const source = record(step) || {};
                return {
                    order: source.order || 0,
                    action: typeof source.action === 'string' ? source.action.slice(0, 160) : '',
                    trade: source.trade || '',
                    duration_days: finiteNonNegative(source.duration_days) || 0,
                };
            }),
            // 2026-08-19: trade entries MUST be rebuilt field-by-field like seq
            // above, never passed through raw. Postgres jsonb normalises object
            // key order (length-then-bytewise), so a raw pass-through hashes
            // {trade, crew_size, role} in memory but {role, trade, crew_size}
            // after any save/load round trip — the SAME scope produced two
            // different hashes and every batch job died at the Stage 3 exit
            // check (WMS_SCOPE_STALE) once the WMS prompt started emitting
            // object-shaped trades_required. String entries pass through
            // unchanged so envelopes stamped before object-trades still verify.
            trades: (Array.isArray(workMethod.trades_required) ? workMethod.trades_required : []).map((entry) => {
                if (typeof entry === 'string') return entry;
                const source = record(entry) || {};
                return {
                    trade: source.trade || '',
                    crew_size: finiteNonNegative(source.crew_size) || 0,
                    role: typeof source.role === 'string' ? source.role.slice(0, 160) : '',
                };
            }),
            access: typeof workMethod.access_strategy === 'string' ? workMethod.access_strategy.slice(0, 200) : '',
            programme_days: finiteNonNegative(workMethod.programme_days) || 0,
        } : null,
    };
}

/**
 * Deterministic hash over method-relevant scope fields. This is change
 * detection, not a cryptographic hash.
 *
 * Deliberately EXCLUDES costBreakdown: Stage 4 pricing recompute and rate-book
 * application rewrite cost lines as part of normal quoting, and a Work Method
 * is not invalidated by a price change. Hashing cost lines (the pre-v5.47.3
 * behaviour) flipped every priced project back to "stale method" and forced a
 * destructive regeneration loop — see computeLegacyScopeHash, kept so
 * previously-stamped envelopes still verify.
 */
export function computeScopeHash(scopeItems: unknown): string {
    if (!Array.isArray(scopeItems)) return '0';
    return djb2Hex(JSON.stringify(scopeItems.map(methodCanon)));
}

/**
 * The pre-v5.47.3 hash (method canon + costBreakdown lines). Never written to
 * new envelopes; retained only so scope_hash values stamped by older builds
 * keep matching an unchanged scope instead of mass-flipping to stale.
 */
export function computeLegacyScopeHash(scopeItems: unknown): string {
    if (!Array.isArray(scopeItems)) return '0';
    const canon = scopeItems.map((item) => {
        const it = record(item) || {};
        const costBreakdown = record(it.costBreakdown);
        return {
            ...methodCanon(item),
            cb: costBreakdown ? {
                materials: (Array.isArray(costBreakdown.materials) ? costBreakdown.materials : [])
                    .map((line) => {
                        const description = record(line)?.description;
                        return typeof description === 'string' ? description.slice(0, 80) : '';
                    }),
                equipment: (Array.isArray(costBreakdown.equipment) ? costBreakdown.equipment : [])
                    .map((line) => {
                        const description = record(line)?.description;
                        return typeof description === 'string' ? description.slice(0, 80) : '';
                    }),
                labourTrades: Array.from(new Set(
                    (Array.isArray(costBreakdown.labour) ? costBreakdown.labour : [])
                        .map((line) => record(line)?.trade || '')
                )).filter(Boolean),
            } : null,
        };
    });
    return djb2Hex(JSON.stringify(canon));
}

/** A stored scope_hash is current if it matches the live scope under either the current or the legacy algorithm. */
export function scopeHashMatches(storedHash: unknown, scopeItems: unknown): boolean {
    if (typeof storedHash !== 'string' || !storedHash) return false;
    return storedHash === computeScopeHash(scopeItems)
        || storedHash === computeLegacyScopeHash(scopeItems);
}

export function isWmsStale(wms: unknown, currentScopeItems: unknown, currentStrategy?: unknown): boolean {
    const envelope = record(wms);
    if (!envelope || !envelope.scope_hash) return false;
    if (!scopeHashMatches(envelope.scope_hash, currentScopeItems)) return true;

    const structured = structuredFrom(envelope);
    // A Work Method rebuilt from the accepted Stage 5 client scope is no
    // longer strategy-authored. Comparing its safe scope title with an older
    // AI strategy label would immediately mark the fresh method stale.
    if (structured?.method_authority === 'final-client-scope') return false;

    const storedStrategyHash = typeof envelope.strategy_hash === 'string'
        ? envelope.strategy_hash.trim()
        : '';
    if (storedStrategyHash) {
        const liveStrategyHash = computeStrategyHash(currentStrategy);
        if (liveStrategyHash && storedStrategyHash !== liveStrategyHash) return true;
    }
    return false;
}

export const WMS_SCOPE_PROJECTION_CONTRACT_VERSION = 1;

/**
 * Re-derive the PM schedules from the final scope. Generation derives them from
 * the activities alone, before Stage 4 prices the client rows those activities
 * belong to, so a bound Work Method would otherwise keep the generation-time
 * picture (partly priced lines, a stale total). Only a Work Method that already
 * carries schedules is refreshed. The activities are not touched: a grouped row
 * holds the combined price of several activities and is read once, never copied
 * onto each of them.
 */
function refreshSchedulesFromScope(
    structured: UnknownRecord | null,
    scope: unknown[],
): UnknownRecord | null {
    if (!structured || !Array.isArray(structured.activities) || !record(structured.schedules)) return structured;
    return { ...structured, schedules: deriveSchedules(structured.activities, { scope }) };
}

function projectAuthoritativeScopePricingIntoActivities(
    structured: UnknownRecord | null,
    scope: unknown[],
): UnknownRecord | null {
    if (!structured || !Array.isArray(structured.activities)) return structured;

    const costBySingleActivityId = new Map<string, unknown>();
    const ambiguousIds = new Set<string>();
    for (const rawItem of scope) {
        const item = record(rawItem);
        const costBreakdown = record(item?.costBreakdown);
        if (!item || !costBreakdown) continue;
        const primaryRef = record(item.activityRef)?.activityId;
        const ids = Array.from(new Set([
            typeof primaryRef === 'string' ? primaryRef : '',
            ...(Array.isArray(item.activityRefs) ? item.activityRefs : []),
            ...(Array.isArray(item.activity_ids) ? item.activity_ids : []),
        ].map(value => String(value || '').trim()).filter(Boolean)));
        // A grouped client row contains the combined price for several
        // activities. Copying that total onto every activity would duplicate
        // it on the next sync, so only bind unambiguous 1:1 projections.
        if (ids.length !== 1) continue;
        const [id] = ids;
        if (!id) continue;
        if (costBySingleActivityId.has(id)) ambiguousIds.add(id);
        else costBySingleActivityId.set(id, costBreakdown);
    }
    for (const id of ambiguousIds) costBySingleActivityId.delete(id);
    if (costBySingleActivityId.size === 0) return structured;

    let changed = false;
    const activities = structured.activities.map((rawActivity) => {
        const activity = record(rawActivity);
        const id = String(activity?.id || '').trim();
        const authoritativeCost = costBySingleActivityId.get(id);
        if (!activity || !authoritativeCost || activity.costBreakdown === authoritativeCost) return rawActivity;
        changed = true;
        return {
            ...activity,
            costBreakdown: authoritativeCost,
        };
    });
    return changed ? { ...structured, activities } : structured;
}

/**
 * Rebind a persisted Work Method to the exact client-facing scope produced
 * from it. `scope_hash` remains the general Stage 3 staleness stamp while the
 * projection receipt proves that Stage 4 (or the deliberate 1:1 no-morph
 * path) finished and persisted this exact scope. Batch preparation must not
 * infer that merely from `scope.length > 0`: Stage 3 itself writes a non-empty
 * provisional scope before an optional 5-activity -> 3-parent consolidation.
 */
export function bindWmsToScopeProjection(
    wms: unknown,
    scope: unknown,
    {
        source = 'scope-morph',
        projectedAt = new Date().toISOString(),
    }: { source?: string; projectedAt?: string } = {},
): UnknownRecord | null {
    const envelope = record(wms);
    if (!envelope || !Array.isArray(scope) || scope.length === 0) return envelope;
    const scopeHash = computeScopeHash(scope);
    const reboundStructured = refreshSchedulesFromScope(
        projectAuthoritativeScopePricingIntoActivities(record(envelope.structured), scope),
        scope,
    );
    const committedPricingCount = scope.filter((rawItem) => {
        const item = record(rawItem);
        return item?.amountAuthority === 'committed' && item?.pricingStatus === 'resolved';
    }).length;
    const methodologyPricingCount = scope.filter((rawItem) => {
        const item = record(rawItem);
        return typeof record(item?.costBreakdown)?.pricingMethodologyId === 'string';
    }).length;
    return {
        ...envelope,
        ...(reboundStructured ? { structured: reboundStructured } : {}),
        scope_hash: scopeHash,
        scope_projection: {
            contract_version: WMS_SCOPE_PROJECTION_CONTRACT_VERSION,
            source,
            scope_hash: scopeHash,
            item_count: scope.length,
            committed_pricing_count: committedPricingCount,
            methodology_pricing_count: methodologyPricingCount,
            projected_at: projectedAt,
        },
    };
}

/** Scope/WMS freshness used by Stage 3 resume logic (projection optional). */
export function isWmsFreshForScope(wms: unknown, scope: unknown): boolean {
    const envelope = record(wms);
    return !!envelope
        && !!record(envelope.structured)
        && Array.isArray(scope)
        && scope.length > 0
        && scopeHashMatches(envelope.scope_hash, scope);
}

/**
 * Strong Stage 4 completion proof: the WMS and its explicit projection
 * receipt must both bind to the exact durable scope.
 */
export function hasFreshWmsScopeProjection(wms: unknown, scope: unknown): boolean {
    if (!isWmsFreshForScope(wms, scope)) return false;
    const envelope = record(wms);
    const projection = record(envelope?.scope_projection);
    return projection?.contract_version === WMS_SCOPE_PROJECTION_CONTRACT_VERSION
        && scopeHashMatches(projection.scope_hash, scope)
        && Number(projection.item_count) === (scope as unknown[]).length;
}

/**
 * True when the ONLY thing wrong with the Work Method is staleness (its
 * scope_hash or chosen-approach label drifted from the live scope/strategy)
 * while the structured method itself is complete and genuine. The navigation
 * layer offers an explicit "Continue with current Method" for this case —
 * regeneration rebuilds scope + pricing, which is destructive mid-quote.
 */
export function isStage3StaleOnly(assessment: Stage3WorkMethodAssessment): boolean {
    return assessment.reasons.length > 0
        && assessment.reasons.every((reason) => reason === 'stale-wms');
}

export function assessStage3WorkMethod({
    wms,
    scope = [],
    strategy = null,
}: {
    wms?: unknown;
    scope?: unknown;
    strategy?: unknown;
} = {}): Stage3WorkMethodAssessment {
    const reasons = new Set<Stage3WorkMethodReason>();
    const envelope = record(wms);
    const structured = structuredFrom(wms);
    const strategyRecord = record(strategy);
    const chosenStrategy = record(strategyRecord?.chosen) || strategyRecord;

    if (!envelope || !record(envelope.structured)) reasons.add('missing-wms');
    if (envelope?.fallback === true) reasons.add('fallback-wms');
    if (strategyRecord?.fallback === true || chosenStrategy?.fallback === true) reasons.add('fallback-strategy');
    if (envelope && structured && isWmsStale(envelope, scope, strategyRecord)) reasons.add('stale-wms');

    const method = structured
        ? assessStage3StructuredMethod(structured)
        : { complete: false, reasons: [] as Stage3WorkMethodReason[], incompleteActivityIds: [] as string[] };
    method.reasons.forEach((reason) => reasons.add(reason));

    return {
        ready: reasons.size === 0,
        reasons: Array.from(reasons),
        programmeDays: getStage3ProgrammeDays(structured),
        incompleteActivityIds: method.incompleteActivityIds,
    };
}

export function stage3ReadinessLabel(assessment: Stage3WorkMethodAssessment): string {
    if (assessment.reasons.includes('missing-wms')) return 'not generated';
    if (assessment.reasons.includes('fallback-strategy')) return 'strategy fallback';
    if (assessment.reasons.includes('fallback-wms')) return 'fallback';
    if (assessment.reasons.includes('stale-wms')) return 'stale';
    if (
        assessment.reasons.includes('missing-sequence')
        || assessment.reasons.includes('placeholder-sequence')
        || assessment.reasons.includes('missing-activities')
    ) return 'incomplete';
    return 'ready';
}
